import { zodResolver } from "@hookform/resolvers/zod";
import { ChevronLeft } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "src/components/form/form";
import { Input } from "src/components/input/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/components/input/Select";
import Section from "src/components/ui/Section";
import { editTrainee } from "src/redux/api/_training.api";
import { Trainee } from "src/redux/types/_trainee.type";
import { EditTraineeSchema } from "src/schemas/edit-trainee";
import { formatDate } from "src/utils";
import { z } from "zod";

interface TraineeDetailsProps {
  defaultView: () => void;
  trainee: Trainee;
}

const EditTrainee = ({ defaultView, trainee }: TraineeDetailsProps) => {
  const form = useForm<z.infer<typeof EditTraineeSchema>>({
    resolver: zodResolver(EditTraineeSchema),
  });

  const [isPending, setIsPending] = useState(false);

  const onSubmit = async (values: z.infer<typeof EditTraineeSchema>) => {
    setIsPending(true);
    try {
      const response = await editTrainee(trainee.id, values);
      if (response.message.toLowerCase() === "success") {
        toast.success("Details Edited Successfully!");
      }
    } catch (error) {
      toast.error("Failed to Save Details!");
      console.error(error);
    } finally {
      setIsPending(false);
    }
  };

  return (
    <div className="max-w-[70%] mx-auto">
      <div className="p-4 flex items-center justify-between shadow-md shadow-slate-400 rounded-md">
        <h1 className="text-2xl font-semibold text-stone-600">Edit Trainee</h1>
        <button
          className="text-lg flex gap-1 items-center border border-gray-600 bg-slate-300 hover:gray-400 px-3 py-2 rounded-lg shadow-md hover:shadow-gray-600"
          onClick={defaultView}
        >
          <ChevronLeft />
          Back
        </button>
      </div>
      <div>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <Section sectionTitle="Training Informations">
              <div className="lg:grid grid-cols-3 gap-4">
                <FormField
                  control={form.control}
                  name="training_program"
                  defaultValue={trainee.training_program}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Training Post Name</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select Post" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {["RWO Training", "DWO Training", "AWO Training"].map(
                            (post) => {
                              return (
                                <SelectItem key={post} value={post}>
                                  {post}
                                </SelectItem>
                              );
                            }
                          )}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                      {!field.value && (
                        <FormDescription className="font-semibold text-red-400">
                          This is a required field
                        </FormDescription>
                      )}
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="preferred_time"
                  defaultValue={trainee.preferred_time}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Preferred Time of Training</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select Time" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {[
                            "Morning: 08:30 am - 11:30 am",
                            "Noon: 12:00 pm - 05:00 pm",
                            "Evening: 06:00 pm - 11:00 pm",
                          ].map((time) => {
                            return (
                              <SelectItem key={time} value={time}>
                                {time}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                      {!field.value && (
                        <FormDescription className="font-semibold text-red-400">
                          This is a required field
                        </FormDescription>
                      )}
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="preferred_training_date"
                  defaultValue={formatDate(trainee.preferred_training_date)}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Preferred Training Date</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="DD-MM-YYYY"
                          {...field}
                          type="date"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </Section>

            <Section sectionTitle="Personal Informations">
              <div className="lg:grid grid-cols-3 gap-4">
                <FormField
                  control={form.control}
                  name="full_name"
                  defaultValue={trainee.full_name}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Full Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Full name" {...field} />
                      </FormControl>
                      <FormMessage />
                      {!field.value && (
                        <FormDescription className="font-semibold text-red-400">
                          This is a required field
                        </FormDescription>
                      )}
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="email"
                  defaultValue={trainee.email}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="example@gmail.com"
                          {...field}
                          type="email"
                        />
                      </FormControl>
                      <FormMessage />
                      {!field.value && (
                        <FormDescription className="font-semibold text-red-400">
                          This is a required field
                        </FormDescription>
                      )}
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="phone_number"
                  defaultValue={trainee.phone_no}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Contact Number</FormLabel>
                      <FormControl>
                        <Input placeholder="1234567890" {...field} />
                      </FormControl>
                      <FormMessage />
                      {!field.value && (
                        <FormDescription className="font-semibold text-red-400">
                          This is a required field
                        </FormDescription>
                      )}
                    </FormItem>
                  )}
                />
              </div>
              <div className="lg:grid grid-cols-2 mt-3">
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="date_of_birth"
                    defaultValue={formatDate(trainee.date_of_birth)}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Date Of Birth (MM-DD-YYYY)</FormLabel>
                        <FormControl>
                          <Input placeholder="" {...field} type="text" />
                        </FormControl>
                        <FormMessage />
                        {!field.value && (
                          <FormDescription className="font-semibold text-red-400">
                            This is a required field
                          </FormDescription>
                        )}
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="gender"
                    defaultValue={trainee.gender}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Gender</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select Gender" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {["Male", "Female", "Other"].map((gender) => {
                              return (
                                <SelectItem key={gender} value={gender}>
                                  {gender}
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                        {!field.value && (
                          <FormDescription className="font-semibold text-red-400">
                            This is a required field
                          </FormDescription>
                        )}
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            </Section>

            <Section sectionTitle="Address Informations">
              <div className="lg:grid grid-cols-3 gap-4">
                <FormField
                  control={form.control}
                  name="city"
                  defaultValue={trainee.city}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>City / Town</FormLabel>
                      <FormControl>
                        <Input placeholder="Guwahati" {...field} />
                      </FormControl>
                      <FormMessage />
                      {!field.value && (
                        <FormDescription className="font-semibold text-red-400">
                          This is a required field
                        </FormDescription>
                      )}
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="building_name"
                  defaultValue={trainee.building_name}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Building Name & House No.</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Building name & house no."
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                      {!field.value && (
                        <FormDescription className="font-semibold text-red-400">
                          This is a required field
                        </FormDescription>
                      )}
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="landmark"
                  defaultValue={trainee.landmark}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Land Mark</FormLabel>
                      <FormControl>
                        <Input placeholder="Land Mark" {...field} />
                      </FormControl>
                      <FormMessage />
                      {!field.value && (
                        <FormDescription className="font-semibold text-red-400">
                          This is a required field
                        </FormDescription>
                      )}
                    </FormItem>
                  )}
                />
              </div>
              <div className="lg:grid grid-cols-3 gap-4 mt-3">
                <FormField
                  control={form.control}
                  name="state"
                  defaultValue={trainee.state}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>State</FormLabel>
                      <FormControl>
                        <Input placeholder="Assam" {...field} />
                      </FormControl>
                      <FormMessage />
                      {!field.value && (
                        <FormDescription className="font-semibold text-red-400">
                          This is a required field
                        </FormDescription>
                      )}
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="district"
                  defaultValue={trainee.district}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>District</FormLabel>
                      <FormControl>
                        <Input placeholder="Kamrup" {...field} />
                      </FormControl>
                      <FormMessage />
                      {!field.value && (
                        <FormDescription className="font-semibold text-red-400">
                          This is a required field
                        </FormDescription>
                      )}
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="postal_code"
                  defaultValue={trainee.pin}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Postal Code</FormLabel>
                      <FormControl>
                        <Input placeholder="666009" {...field} />
                      </FormControl>
                      <FormMessage />
                      {!field.value && (
                        <FormDescription className="font-semibold text-red-400">
                          This is a required field
                        </FormDescription>
                      )}
                    </FormItem>
                  )}
                />
              </div>
            </Section>
            <div className="py-4 mt-8 flex items-center justify-center rounded-md shadow-md shadow-slate-400">
              <button
                className="btn btn-wide bg-sky-400 text-white font-semibold hover:text-sky-500"
                type="submit"
              >
                {isPending ? (
                  <span className="loading loading-spinner text-white"></span>
                ) : (
                  "SAVE DETAILS"
                )}
              </button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
};

export default EditTrainee;
